import { CheckCircleOutlined, ChevronRightOutlined, CancelOutlined } from '@mui/icons-material';
import {
  Checkbox,
  ListItemButton,
  ListItemIcon,
  ListItemProps,
  ListItemText,
  PopoverOrigin,
  Typography,
} from '@mui/material';
import { useUuid } from '@procureai/ui-mui';
import { bindHover, bindMenu, usePopupState } from 'material-ui-popup-state/hooks';
import HoverMenu from 'material-ui-popup-state/HoverMenu';
import { useTranslation } from 'react-i18next';

import { DemandCasesReleaseQuery } from '@/generated/gql/graphql';
import { Address } from '~/components/Address/Address';
import { NegotiationCaseIsReleasableCellMenu } from '~/components/NegotiationCaseIsReleasableCellMenu/NegotiationCaseIsReleasableCellMenu';

import { ListItemStyled } from './CaseReleaseListItem.styled';
import { userNameLabel } from './CaseReleaseListItem.util';

const ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: 'center',
  horizontal: 'right',
};
const TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: 'center',
  horizontal: 'right',
};

export interface CaseReleaseListItemProps extends Omit<ListItemProps, 'selected' | 'onSelect'> {
  demandId?: string;
  item: DemandCasesReleaseQuery['tesseract']['demand']['negotiationCases']['nodes'][number];
  selected: boolean;
  onSelect?: (id: string) => void;
}

export const CaseReleaseListItem = ({
  demandId,
  item,
  selected,
  onSelect,
  ...otherProps
}: CaseReleaseListItemProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'DemandCasesBatchReleaseDialog' });
  const popupId = useUuid();
  const popupState = usePopupState({ variant: 'popover', popupId });

  const handleToggle = () => {
    onSelect?.(item.id);
  };
  return (
    <>
      <ListItemStyled
        {...otherProps}
        disablePadding={true}
        secondaryAction={
          item.isReleasable ? (
            <Checkbox edge="end" color="success" onChange={handleToggle} checked={selected} />
          ) : (
            <ChevronRightOutlined />
          )
        }
      >
        <ListItemButton
          selected={selected}
          onClick={item.isReleasable ? handleToggle : undefined}
          {...(item.isReleasable ? {} : bindHover(popupState))}
        >
          <ListItemIcon>
            {item.isReleasable ? <CheckCircleOutlined color="success" /> : <CancelOutlined color="error" />}
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="subtitle1">
                {item.supplier.name}{' '}
                <Typography variant="caption">
                  <Address
                    street={item.supplier.location.address}
                    city={item.supplier.location.city}
                    postalCode={item.supplier.location.postalCode}
                    countryCode={item.supplier.location.countryCode}
                  />
                </Typography>
              </Typography>
            }
            secondary={item.isReleasable ? userNameLabel(item.supplier.contact) : t('missingInformation')}
            slotProps={{
              secondary: item.isReleasable ? undefined : { color: 'error' },
            }}
          />
        </ListItemButton>
        {item.isReleasable ? null : (
          <HoverMenu {...bindMenu(popupState)} anchorOrigin={ANCHOR_ORIGIN} transformOrigin={TRANSFORM_ORIGIN}>
            <NegotiationCaseIsReleasableCellMenu
              demandId={demandId ?? ''}
              negotiationCaseId={item.id}
              list={item.processSteps}
            />
          </HoverMenu>
        )}
      </ListItemStyled>
    </>
  );
};
